/**
 * TevmBackendProvider — adapts Tevm's in-browser EVM to the shared
 * EvmBackendContext interface consumed by @gotts.ai/ui/devtools views.
 *
 * Mirrors AnvilBackendProvider but uses Tevm's memory transport
 * instead of HTTP/WS connections to an Anvil process.
 */

import { EvmBackendContext } from "@gotts.ai/ui/devtools";
import { useCallback, useMemo, useState } from "react";
import { createWalletClient, custom, encodeFunctionData } from "viem";
import { privateKeyToAccount } from "viem/accounts";

import { PREFUNDED_ACCOUNTS } from "../runtime/accounts.js";
import {
  fundAccountWithToken,
  TOKEN_BALANCE_SLOTS,
} from "../seed/fork-seed.js";

import { useRequiredTevmContext } from "./TevmContext.js";

import type { DevAccount, EvmBackendContextValue } from "@gotts.ai/ui/devtools";
import type { Address, EIP1193Provider, Hex, WalletClient } from "viem";

// ---------------------------------------------------------------------------
// Accounts: map PrefundedAccount (privateKey) → DevAccount (key)
// ---------------------------------------------------------------------------

const accounts: readonly DevAccount[] = PREFUNDED_ACCOUNTS.slice(0, 10).map(
  (a) => ({
    address: a.address,
    key: a.privateKey,
    label: a.label,
  }),
);

// ---------------------------------------------------------------------------
// Wallet persistence (matches devenv's pattern with different storage key)
// ---------------------------------------------------------------------------

type WalletMode = "disconnected" | "tevm" | "private-key" | "injected";
const STORAGE_KEY = "webenv-wallet";

interface StoredWallet {
  mode: WalletMode;
  address: Address;
  label: string;
  key?: Hex;
}

function loadStoredWallet(): StoredWallet | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredWallet;
  } catch {
    return null;
  }
}

function saveStoredWallet(wallet: StoredWallet | null): void {
  if (wallet) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(wallet));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

// ---------------------------------------------------------------------------
// Wallet state
// ---------------------------------------------------------------------------

interface WalletState {
  address: Address | null;
  label: string;
  walletClient: WalletClient | null;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function TevmBackendProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    publicClient,
    walletClient: defaultWalletClient,
    testClient,
    chain,
    deployment,
  } = useRequiredTevmContext();

  // Helper: create a walletClient that routes through Tevm's memory transport.
  // Uses privateKeyToAccount for known prefunded accounts so signing operations
  // (signTypedData, signMessage) happen locally instead of via RPC — Tevm doesn't
  // handle wallet methods like eth_signTypedData_v4.
  const makeTevmWalletClient = useCallback(
    (addr: Address) => {
      const prefunded = PREFUNDED_ACCOUNTS.find(
        (a) => a.address.toLowerCase() === addr.toLowerCase(),
      );
      const account = prefunded
        ? privateKeyToAccount(prefunded.privateKey)
        : addr;
      return createWalletClient({
        account,
        chain,
        transport: custom({
          request: async ({
            method,
            params,
          }: {
            method: string;
            params?: unknown[];
          }) => {
            return publicClient.transport.request({
              method,
              params,
            } as { method: string; params: unknown[] });
          },
        }),
      });
    },
    [chain, publicClient],
  );

  // Initialize wallet from localStorage or default to deployer
  const [wallet, setWallet] = useState<WalletState>(() => {
    const stored = loadStoredWallet();

    if (stored?.mode === "tevm") {
      void testClient.impersonateAccount({ address: stored.address });
      return {
        address: stored.address,
        label: stored.label,
        walletClient: makeTevmWalletClient(stored.address),
      };
    }

    if (stored?.mode === "private-key" && stored.key) {
      const account = privateKeyToAccount(stored.key);
      return {
        address: account.address,
        label: "Private Key",
        walletClient: createWalletClient({
          account,
          chain,
          transport: custom({
            request: async ({
              method,
              params,
            }: {
              method: string;
              params?: unknown[];
            }) => {
              return publicClient.transport.request({
                method,
                params,
              } as { method: string; params: unknown[] });
            },
          }),
        }),
      };
    }

    // Default: deployer (accounts[0])
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const deployer = accounts[0]!;
    void testClient.impersonateAccount({ address: deployer.address });
    return {
      address: deployer.address,
      label: deployer.label,
      walletClient: defaultWalletClient,
    };
  });

  // -----------------------------------------------------------------------
  // Wallet actions
  // -----------------------------------------------------------------------

  const connectAnvil = useCallback(
    (addr: Address, lbl: string) => {
      void testClient.impersonateAccount({ address: addr });
      const client = makeTevmWalletClient(addr);
      setWallet({ address: addr, label: lbl, walletClient: client });
      saveStoredWallet({ mode: "tevm", address: addr, label: lbl });
    },
    [testClient, makeTevmWalletClient],
  );

  const connectPrivateKey = useCallback(
    (key: Hex) => {
      const account = privateKeyToAccount(key);
      const client = createWalletClient({
        account,
        chain,
        transport: custom({
          request: async ({
            method,
            params,
          }: {
            method: string;
            params?: unknown[];
          }) => {
            return publicClient.transport.request({
              method,
              params,
            } as { method: string; params: unknown[] });
          },
        }),
      });
      setWallet({
        address: account.address,
        label: "Private Key",
        walletClient: client,
      });
      saveStoredWallet({
        mode: "private-key",
        address: account.address,
        label: "Private Key",
        key,
      });
    },
    [chain, publicClient],
  );

  const connectInjected = useCallback(async () => {
    const ethereum = (window as unknown as Record<string, unknown>).ethereum as
      | EIP1193Provider
      | undefined;
    if (!ethereum) throw new Error("No injected wallet found");

    const requestedAccounts = await ethereum.request({
      method: "eth_requestAccounts",
    });
    const addr = requestedAccounts[0];
    if (!addr) throw new Error("No accounts returned");

    const client = createWalletClient({
      account: addr,
      chain,
      transport: custom(ethereum),
    });
    setWallet({ address: addr, label: "Browser Wallet", walletClient: client });
    saveStoredWallet({
      mode: "injected",
      address: addr,
      label: "Browser Wallet",
    });
  }, [chain]);

  const disconnect = useCallback(() => {
    setWallet({ address: null, label: "", walletClient: null });
    saveStoredWallet(null);
  }, []);

  const rpcRequest = useCallback(
    async (method: string, params?: unknown[]) => {
      return publicClient.transport.request({
        method,
        params: params ?? [],
      } as { method: string; params: unknown[] });
    },
    [publicClient],
  );

  // -----------------------------------------------------------------------
  // Token minting (storage slot for fork mode, mint() fallback for scratch)
  // -----------------------------------------------------------------------

  const mintToken = useCallback(
    async (tokenAddress: Address, account: Address, amount: bigint) => {
      // Check if we have a known storage slot for this token
      const slotEntry = Object.values(TOKEN_BALANCE_SLOTS).find(
        (entry) => entry.address.toLowerCase() === tokenAddress.toLowerCase(),
      );

      if (slotEntry) {
        // Fork mode: use storage slot manipulation
        await fundAccountWithToken(
          testClient,
          tokenAddress,
          slotEntry.slot,
          account,
          amount,
        );
      } else {
        // From-scratch mode: call mint() via impersonated deployer
        const deployer = accounts[0];
        if (!deployer) return;
        const calldata = encodeFunctionData({
          abi: [
            {
              type: "function",
              name: "mint",
              inputs: [
                { name: "to", type: "address" },
                { name: "amount", type: "uint256" },
              ],
              outputs: [],
              stateMutability: "nonpayable",
            },
          ] as const,
          functionName: "mint",
          args: [account, amount],
        });
        await testClient.request({
          method: "anvil_impersonateAccount" as never,
          params: [deployer.address] as never,
        });
        await publicClient.transport.request({
          method: "eth_sendTransaction",
          params: [
            {
              from: deployer.address,
              to: tokenAddress,
              data: calldata,
            },
          ],
        });
        await testClient.request({
          method: "anvil_stopImpersonatingAccount" as never,
          params: [deployer.address] as never,
        });
      }
    },
    [testClient, publicClient],
  );

  // -----------------------------------------------------------------------
  // Context value
  // -----------------------------------------------------------------------

  const value = useMemo<EvmBackendContextValue>(
    () => ({
      publicClient:
        publicClient as unknown as EvmBackendContextValue["publicClient"],
      walletClient: wallet.walletClient,
      httpClient:
        publicClient as unknown as EvmBackendContextValue["httpClient"],
      testClient: testClient as unknown as EvmBackendContextValue["testClient"],
      chain,
      address: wallet.address,
      label: wallet.label,
      deployment: deployment as unknown as EvmBackendContextValue["deployment"],
      deploymentLoading: false,
      accounts,
      connectAnvil,
      connectPrivateKey,
      connectInjected,
      disconnect,
      rpcRequest,
      mintToken,
    }),
    [
      publicClient,
      wallet,
      testClient,
      chain,
      deployment,
      connectAnvil,
      connectPrivateKey,
      connectInjected,
      disconnect,
      rpcRequest,
      mintToken,
    ],
  );

  return (
    <EvmBackendContext.Provider value={value}>
      {children}
    </EvmBackendContext.Provider>
  );
}
